import { create } from 'zustand';

export type ToastVariant = 'success' | 'error';

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  createdAt: number;
  durationMs: number;
}

interface ToastState {
  toasts: ToastItem[];
  push: (toast: Omit<ToastItem, 'id' | 'createdAt'> & { id?: string }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

function newToastId(): string {
  // Small, non-crypto id for UI-only state.
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  push: (toast) => {
    const id = toast.id ?? newToastId();
    const item: ToastItem = {
      id,
      variant: toast.variant,
      title: toast.title,
      description: toast.description,
      durationMs: toast.durationMs,
      createdAt: Date.now(),
    };

    // Cap to avoid unbounded growth if callers spam.
    const next = [item, ...get().toasts].slice(0, 5);
    set({ toasts: next });
    return id;
  },

  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),

  clear: () => set({ toasts: [] }),
}));

export const toast = {
  success: (input: { title: string; description?: string; durationMs?: number }) =>
    useToastStore.getState().push({
      variant: 'success',
      title: input.title,
      description: input.description,
      durationMs: input.durationMs ?? 4000,
    }),
  error: (input: { title: string; description?: string; durationMs?: number }) =>
    useToastStore.getState().push({
      variant: 'error',
      title: input.title,
      description: input.description,
      durationMs: input.durationMs ?? 6000,
    }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
  clear: () => useToastStore.getState().clear(),
};
